/* eslint-disable import/prefer-default-export */
import {
  includes,
  replace,
  reduce,
  forEach,
  has,
  keys,
  find,
  isEmpty,
  findLastIndex
} from 'lodash'
import type { AccountInfo } from '@src/endpoints/services/base.service'
import type {
  Organization,
  Organizations,
  SingleAccounts,
  SubOrganization
} from '@src/redux/reducers/auth/models'

export type Data = {
  singleAccounts: SingleAccounts
  organizations: Organizations
}

export const uuid4Regex =
  /[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}/

export function getScopeDisplayName(value: string) {
  let scope = value
  const providers = '/providers/Microsoft.Management'

  if (!scope) {
    return scope
  }

  if (includes(scope, providers)) {
    scope = replace(scope, providers, '')
  }

  if (scope.includes('SCOPE')) {
    const scopeArray = scope.split('/')
    const index = scopeArray.indexOf('SCOPE')
    scope = scopeArray.slice(index + 1).join('/')
  }

  return scope
}

export function shortenPath(path: string) {
  if (!isEmpty(path)) {
    const parts = path.split('/')
    if (parts.length > 1) {
      if (parts.length === 2 && parts[0] === parts[1]) return 'global'
      // Azure
      const scopeTypes = ['subscriptions', 'managementgroups', 'resourcegroups']
      const found = findLastIndex(parts, p =>
        scopeTypes.includes(p.toLowerCase())
      )
      if (found >= 0) {
        const scopeType = parts[found]
        const scopeId = parts[found + 1]
        return `/${scopeType}/${scopeId}${
          parts.length - found > 2 ? '/RESOURCE' : ''
        }`
      }
    }
  }
  return null
}

export class Scope {
  private

  _value: string

  constructor(value: string) {
    this._value = value
  }

  replaceAccountID(
    singleAccounts: SingleAccounts,
    organizations: Organizations
  ): Scope {
    const { accountsByAccountId, organizationsByOrgId, subOrganizationsById } =
      Scope.getStoreData(singleAccounts, organizations)

    if (has(accountsByAccountId, this._value)) {
      this._value = accountsByAccountId[this._value].account_name
    }

    if (has(organizationsByOrgId, this._value)) {
      this._value = organizationsByOrgId[this._value].organization_name
    }

    if (has(subOrganizationsById, this._value)) {
      this._value = subOrganizationsById[this._value].suborganization_name
    }

    return this
  }

  replaceManagementGroupsID(organizations: Organizations) {
    if (!this._value) {
      return this
    }
    const { entities } = organizations || {}
    const { organizations: organizationList } = entities || {}

    const stringArray = this._value.split('/')
    const mgKeyword = 'managementGroups'

    const keywordIndex = stringArray.indexOf(mgKeyword)
    const mgID = keywordIndex === -1 ? null : stringArray[keywordIndex + 1]

    if (mgID === null || !uuid4Regex.test(mgID)) {
      return this
    }
    const organization = find(
      organizationList,
      item => item.azure_organization_info?.tenant_id === mgID
    )

    if (organization) {
      stringArray[keywordIndex + 1] = organization.organization_name
    }

    this._value = stringArray.join('/')
    return this
  }

  replaceSubscriptionID(singleAccounts: SingleAccounts) {
    if (!this._value) {
      return this
    }
    const { accountsByAccountId } = Scope.getStoreData(singleAccounts, null)
    const stringArray = this._value.split('/')
    const subscriptionsKeyword = 'subscriptions'

    const keywordIndex = stringArray.indexOf(subscriptionsKeyword)
    const subscriptionID =
      keywordIndex === -1 ? null : stringArray[keywordIndex + 1]

    if (subscriptionID === null) {
      return this
    }

    const IDs = keys(accountsByAccountId)
    const IDkey = find(IDs, item => item.includes(subscriptionID))

    if (IDkey && has(accountsByAccountId, IDkey)) {
      stringArray[keywordIndex + 1] = accountsByAccountId[
        IDkey
      ].account_name.replace(/ *\([^)]*\) */g, '')
    }

    this._value = stringArray.join('/')
    return this
  }

  replaceFolderID(
    singleAccounts: SingleAccounts,
    organizations: Organizations
  ): Scope {
    if (!this._value) {
      return this
    }

    const { subOrganizationsById } = Scope.getStoreData(
      singleAccounts,
      organizations
    )
    const stringArray = this._value.split('/')
    const foldersKeyword = 'folders'

    const matches = reduce(
      stringArray,
      (acc, current, index) => {
        if (current === foldersKeyword) {
          acc.push(index)
        }
        return acc
      },
      []
    )

    forEach(matches, foldersIndex => {
      const foldersID = stringArray[foldersIndex + 1]
      const foldersKey = `${foldersKeyword}/${foldersID}`

      if (foldersID && has(subOrganizationsById, foldersKey)) {
        stringArray[foldersIndex + 1] =
          subOrganizationsById[foldersKey].suborganization_name
      }
    })

    this._value = stringArray.join('/')
    return this
  }

  replaceOrganizationID(
    singleAccounts: SingleAccounts,
    organizations: Organizations
  ): Scope {
    if (!this._value) {
      return this
    }

    const { organizationsByOrgId } = Scope.getStoreData(
      singleAccounts,
      organizations
    )
    const stringArray = this._value.split('/')
    const orgsKeyword = 'organizations'

    const keywordIndex = stringArray.indexOf(orgsKeyword)
    const organizationID =
      keywordIndex === -1 ? null : stringArray[keywordIndex + 1]

    if (organizationID && has(organizationsByOrgId, organizationID)) {
      stringArray[keywordIndex + 1] =
        organizationsByOrgId[organizationID].organization_name
    }

    this._value = stringArray.join('/')
    return this
  }

  parse(data: Data, short = false) {
    const { singleAccounts, organizations } = data
    this.replaceAccountID(singleAccounts, organizations)
      .replaceOrganizationID(singleAccounts, organizations)
      .replaceFolderID(singleAccounts, organizations)
      .replaceSubscriptionID(singleAccounts)
      .replaceManagementGroupsID(organizations)

    if (short) {
      const shortPath = shortenPath(this._value)
      this._value = shortPath || this._value
    }

    return this
  }

  get value(): string {
    return this._value
  }

  private static getStoreData(
    singleAccounts: SingleAccounts,
    organizations: Organizations
  ): {
    accountsByAccountId: { [key: string]: AccountInfo }
    organizationsByOrgId: { [key: string]: Organization }
    subOrganizationsById: { [key: string]: SubOrganization }
  } {
    const { entities } = singleAccounts || {}
    const { accountsByAccountId } = entities || {}
    const { entities: organizationsEntities } = organizations || {}
    const { organizationsByOrgId } = organizationsEntities || {}
    const { subOrganizationsById } = organizationsEntities || {}

    return { accountsByAccountId, organizationsByOrgId, subOrganizationsById }
  }
}

export function createScopeInstance(scope: string) {
  return new Scope(scope)
}
